import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    TbQuestionMark,
    TbX,
    TbChevronDown,
    TbBuildingBank,
    TbCreditCard,
    TbFileUpload,
    TbFilePlus,
    TbSend,
    TbPalette,
    TbBellRinging,
    TbCurrencyEuro,
    TbBook,
    TbTags,
    TbUsersGroup,
    TbChartBar,
    TbShieldCheck,
    TbReportAnalytics,
    TbFolderSymlink,
    TbTableImport,
} from "react-icons/tb";
import "./quickTips.scss";

interface TipSection {
    heading: string;
    steps: string[];
}

interface TipItem {
    key: string;
    icon: React.ReactNode;
    steps?: string[];
    sections?: TipSection[];
    action?: { label: string; to: string };
    /** Plan tier (2=Plus, 3=Pro) this feature requires, if any — shown as a badge on the title. */
    minPlan?: 2 | 3;
}

// Back on once SumUp has enabled payments for our app (SUMUP_PAYMENTS_ENABLED) — the tip leads to connecting.
const CARD_PAYMENT_LIVE = false;

export default function QuickTips() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);

    const toggle = (key: string) =>
        setExpanded((prev) => (prev === key ? null : key));

    const items: TipItem[] = [
        {
            key: "bank",
            icon: <TbBuildingBank />,
            steps: t("quickTips.bank.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.bank.action"), to: "/settings" },
        },
        ...(CARD_PAYMENT_LIVE
            ? [{
                key: "cardPayment",
                icon: <TbCreditCard />,
                steps: t("quickTips.cardPayment.steps", { returnObjects: true }) as string[],
                action: { label: t("quickTips.cardPayment.action"), to: "/settings?section=sumup" },
            }]
            : []),
        {
            key: "pad",
            icon: <TbFileUpload />,
            steps: t("quickTips.pad.steps", { returnObjects: true }) as string[],
        },
        {
            key: "createInvoice",
            icon: <TbFilePlus />,
            steps: t("quickTips.createInvoice.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.createInvoice.action"), to: "/invoices/new" },
        },
        {
            key: "csvImport",
            icon: <TbTableImport />,
            steps: t("quickTips.csvImport.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.csvImport.action"), to: "/patients?import=csv" },
        },
        {
            key: "praxisSoftwareSync",
            icon: <TbFolderSymlink />,
            steps: t("quickTips.praxisSoftwareSync.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.praxisSoftwareSync.action"), to: "/settings?section=praxis-software-sync" },
        },
        {
            key: "send",
            icon: <TbSend />,
            sections: t("quickTips.send.sections", { returnObjects: true }) as TipSection[],
            action: { label: t("quickTips.send.action"), to: "/invoices" },
        },
        {
            key: "personalize",
            icon: <TbPalette />,
            steps: t("quickTips.personalize.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.personalize.action"), to: "/settings" },
        },
        {
            key: "dunning",
            icon: <TbBellRinging />,
            steps: t("quickTips.dunning.steps", { returnObjects: true }) as string[],
        },
        {
            key: "charges",
            icon: <TbCurrencyEuro />,
            steps: t("quickTips.charges.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.charges.action"), to: "/charges" },
        },
        {
            key: "tags",
            icon: <TbTags />,
            steps: t("quickTips.tags.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.tags.action"), to: "/invoices" },
            minPlan: 2,
        },
        {
            key: "groups",
            icon: <TbUsersGroup />,
            steps: t("quickTips.groups.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.groups.action"), to: "/patients" },
        },
        {
            key: "statistics",
            icon: <TbChartBar />,
            steps: t("quickTips.statistics.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.statistics.action"), to: "/main" },
        },
        {
            key: "goaChecker",
            icon: <TbShieldCheck />,
            steps: t("quickTips.goaChecker.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.goaChecker.action"), to: "/invoices" },
            minPlan: 3,
        },
        {
            key: "reports",
            icon: <TbReportAnalytics />,
            steps: t("quickTips.reports.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.reports.action"), to: "/settings" },
            minPlan: 3,
        },
        {
            key: "docs",
            icon: <TbBook />,
            steps: t("quickTips.docs.steps", { returnObjects: true }) as string[],
            action: { label: t("quickTips.docs.action"), to: "/docs" },
        },
    ];

    return (
        <div className="qt">
            {open && (
                <div className="qt__panel">
                    <div className="qt__header">
                        <span className="qt__title">{t("quickTips.title")}</span>
                        <button className="qt__close" onClick={() => setOpen(false)} aria-label="Schließen">
                            <TbX />
                        </button>
                    </div>
                    <ul className="qt__list">
                        {items.map((item) => {
                            const isOpen = expanded === item.key;
                            return (
                                <li key={item.key} className={`qt__item${isOpen ? " qt__item--open" : ""}`}>
                                    <button
                                        className="qt__item-trigger"
                                        onClick={() => toggle(item.key)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="qt__item-icon">{item.icon}</span>
                                        <span className="qt__item-label">{t(`quickTips.${item.key}.title`)}</span>
                                        {item.minPlan && (
                                            <span className={`qt__plan-badge qt__plan-badge--${item.minPlan}`}>
                                                {t(`quickTips.planBadge.${item.minPlan}`)}
                                            </span>
                                        )}
                                        <TbChevronDown className="qt__item-chevron" />
                                    </button>
                                    {isOpen && (
                                        <div className="qt__item-body">
                                            {item.sections ? (
                                                item.sections.map((sec, si) => (
                                                    <div key={si} className="qt__section">
                                                        <span className="qt__section-heading">{sec.heading}</span>
                                                        <ol className="qt__steps">
                                                            {sec.steps.map((step, i) => (
                                                                <li key={i}>{step}</li>
                                                            ))}
                                                        </ol>
                                                    </div>
                                                ))
                                            ) : (
                                                <ol className="qt__steps">
                                                    {(item.steps ?? []).map((step, i) => (
                                                        <li key={i}>{step}</li>
                                                    ))}
                                                </ol>
                                            )}
                                            {item.action && (
                                                <button
                                                    className="qt__action-btn"
                                                    onClick={() => {
                                                        navigate(item.action!.to);
                                                        setOpen(false);
                                                    }}
                                                >
                                                    {item.action.label}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            <button
                className={`qt__fab${open ? " qt__fab--active" : ""}`}
                onClick={() => setOpen((v) => !v)}
                aria-label={t("quickTips.title")}
                title={t("quickTips.title")}
            >
                <TbQuestionMark />
            </button>
        </div>
    );
}
